import type { LeadProposalGenerationType, LeadType } from '@econtractor/types';
import { safeFloatParse } from 'utils/helpers/safeParsing';
import type { NewObjectType } from '../types';

const roundMoney = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;

/**
 * Сума КП у валюті поля objectPrice (USD, або UAH при курсі 1).
 * Якщо КП у грн і курс !== 1 — перерахунок у долари.
 */
export const proposalAmountToObjectPrice = (
    generation?: LeadProposalGenerationType | null,
    rate: number = 1,
): number => {
    const amount = safeFloatParse(generation?.proposalAmount || 0) || 0;
    if (!amount) return 0;

    if (Boolean(generation?.proposalUsd)) return roundMoney(amount);

    const currentRate = safeFloatParse(rate) || 1;
    if (currentRate !== 1 && currentRate > 0) {
        return roundMoney(amount / currentRate);
    }

    return roundMoney(amount);
};

/** Назва об'єкта в офканвасі: назва ліда, якщо заповнена, інакше ім'я клієнта. */
const defaultObjectNameFromLead = (lead: LeadType): string => {
    const leadName = String(lead.leadName || '').trim();
    if (leadName) return leadName;
    return String(lead.clientName || '').trim();
};

export const buildNewObjectFromLead = (
    lead: LeadType,
    generation?: LeadProposalGenerationType | null,
    rate: number = 1,
): NewObjectType => {
    const objectPrice = proposalAmountToObjectPrice(generation, rate);
    const currentRate = safeFloatParse(rate) || 1;

    return {
        leadId: lead.ID,
        objectName: defaultObjectNameFromLead(lead),
        clientId: lead.clientId,
        clientName: lead.clientName,
        clientPhone: lead.clientPhone,
        clientEmail: lead.clientEmail,
        objectPrice,
        objectPriceRate: roundMoney(objectPrice * currentRate),
    };
};

export const buildNewSaleFromLead = (
    lead: LeadType,
    generation?: LeadProposalGenerationType | null,
    rate: number = 1,
): NewObjectType => {
    const base = buildNewObjectFromLead(lead, generation, rate);
    return { ...base, onlySale: true };
};

export const buildNewServiceSaleFromLead = (
    lead: LeadType,
    generation?: LeadProposalGenerationType | null,
    rate: number = 1,
): NewObjectType => {
    const base = buildNewObjectFromLead(lead, generation, rate);
    return { ...base, onlySale: false, onlyService: true };
};

/** Підставляє суму з активної КП, якщо в формі ще немає ціни. */
export const syncNewObjectPriceFromProposal = (
    prev: NewObjectType,
    generation?: LeadProposalGenerationType | null,
    rate: number = 1,
): NewObjectType => {
    const nextPrice = proposalAmountToObjectPrice(generation, rate);
    if (!nextPrice) return prev;

    const currentPrice = safeFloatParse(prev.objectPrice || 0) || 0;
    if (currentPrice > 0) return prev;

    const currentRate = safeFloatParse(rate) || 1;
    return {
        ...prev,
        objectPrice: nextPrice,
        objectPriceRate: roundMoney(nextPrice * currentRate),
    };
};
